import { h, Component, Prop, Element, Host } from '@stencil/core';

import { getCSSVariable } from '../../../services/utils';

/**
 * An empty message is used to allow users to understand that their action is not retrieving any results in the system.
 *
 * @slot title - The slot for the primary text, e.g.: Heading, Title, etc.
 * @slot action - The slot for the recovery action.
 * @slot - The default slot for the text description to give more details.
 *
 * @part base - Div wrapper container inside the shadow DOM that hold each `slot` section.
 */
@Component({
  tag: 'pk-empty-message',
  styleUrl: './scss/pk-empty-message.scss',
  shadow: true,
})
export class PkEmptyMessage {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el: HTMLPkEmptyMessageElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** Set the background image */
  @Prop() bgImg: string;

  /** Set the background color */
  @Prop() bgColor: string;

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.bgColor && {
        '--empty-message--bg-color': getCSSVariable(this.bgColor),
      }),
      ...(this.bgImg && {
        '--empty-message--bg-image': `url(${this.bgImg})`,
      }),
    };

    return (
      <Host style={{ ...style }}>
        <div class="pk-empty-message__base" part="base">
          <slot name="title" />
          <slot />
          <slot name="action" />
        </div>
      </Host>
    );
  }
}
